import { z } from "zod";

// Weather for one action's area (#326): a forecast for the HQ panel, with the emphasis on what is
// COMING — rain, thunderstorm, wind gusts — so a drone can land before it arrives. Source is
// Open-Meteo (CC BY 4.0, no key); the backend fetches through a proxy (lib/open-meteo.ts) and caches
// per action so viewer count never adds API calls. A WARNING/orientation aid, never an authority:
// the flight call is the pilot's and the drone maker's limits come first ("pomoc, nie wyrocznia").

/** Minutes a forecast is served from cache before we ask Open-Meteo again. The ICON model refreshes
 * every ~3 h and the 15-min nowcast steps within it, so a quarter-hour is fresh enough and keeps us
 * far under the free tier (one call per action per 15 min). */
export const WEATHER_CACHE_MIN = 15;
/** CC BY 4.0 requires credit with a link to the source; this is the wording Open-Meteo asks for. */
export const WEATHER_ATTRIBUTION = "Pogoda: Open-Meteo.com";

/**
 * Shown with every forecast and with the flight indicator: the data is a model forecast, not a
 * measurement, and the decision to fly is the pilot's. Keeps it from reading as an authority.
 */
export const WEATHER_DISCLAIMER_PL =
  "Prognoza (model Open-Meteo), nie pomiar. Decyzję o locie podejmuje pilot — limity drona są nadrzędne.";

/** Flight advice from the forecast — one of three lights at a glance. */
export const FlightAdvice = z.enum(["ok", "caution", "noFly"]);
export type FlightAdvice = z.infer<typeof FlightAdvice>;

/** The conditions that drive the flight light (gusts, near-term rain, storm risk). Thresholds are a
 * starting point for a consumer drone (e.g. DJI); calibrate on real flights. m/s and the next ~2 h. */
export const FLIGHT_THRESHOLDS = {
  /** Wind gusts (m/s) over this → no-fly; over the caution value → caution. ~10 m/s is a common
   * consumer-drone wind limit, so stop above it and warn as it is approached. */
  gustNoFly: 12,
  gustCaution: 9,
  /** Precipitation probability (%) in the next ~2 h: over this → no-fly; over caution → caution. */
  rainNoFly: 60,
  rainCaution: 25,
  /** CAPE (J/kg): convective energy as a thunderstorm-risk proxy (no free lightning data exists).
   * Over this with a thundery weather code → no-fly; over caution → caution. */
  capeNoFly: 1500,
  capeCaution: 500,
} as const;

/** WMO weather codes 95/96/99 are thunderstorm (Open-Meteo `weather_code`). Used with CAPE so a
 * forecast that already calls it a storm is not downgraded by a low CAPE value. */
export const THUNDER_WMO_CODES = [95, 96, 99] as const;
export function isThunderCode(code: number): boolean {
  return (THUNDER_WMO_CODES as readonly number[]).includes(code);
}

/**
 * The flight light from the forecast, and why. Pure so it is tested once and used on both the
 * backend (to send a reason) and the panel. `caution` wins over `ok`, `noFly` over everything; the
 * reason names the worst driver. Thresholds in FLIGHT_THRESHOLDS.
 */
export function flightAdvice(f: {
  gusts: number;
  /** Max precipitation probability over the near term (%), or null when unknown. */
  rainChance: number | null;
  cape: number;
  weatherCode: number;
}): { advice: FlightAdvice; reason: FlightReason } {
  const t = FLIGHT_THRESHOLDS;
  const thundery = isThunderCode(f.weatherCode);
  // No-fly: any single hard limit crossed.
  if (f.gusts > t.gustNoFly) return { advice: "noFly", reason: "gust" };
  if (f.rainChance != null && f.rainChance > t.rainNoFly)
    return { advice: "noFly", reason: "rain" };
  if (thundery || f.cape > t.capeNoFly) return { advice: "noFly", reason: "storm" };
  // Caution: approaching a limit.
  if (f.gusts > t.gustCaution) return { advice: "caution", reason: "gust" };
  if (f.rainChance != null && f.rainChance > t.rainCaution)
    return { advice: "caution", reason: "rain" };
  if (f.cape > t.capeCaution) return { advice: "caution", reason: "storm" };
  return { advice: "ok", reason: "clear" };
}

/** Why the flight light is what it is (for the UI word). */
export const FlightReason = z.enum(["clear", "gust", "rain", "storm"]);
export type FlightReason = z.infer<typeof FlightReason>;

/** One forecast for an action's area centre. Values are current conditions plus the near-term
 * outlook that drives the flight light; the panel renders the numbers and the light. */
export const WeatherForecast = z.object({
  /** Area centre the forecast is for (midpoint of the action bbox). */
  lat: z.number(),
  lon: z.number(),
  /** Current temperature, °C. */
  temperature: z.number(),
  /** Current precipitation, mm (this 15-min/hour step). */
  precipitation: z.number(),
  /** Max precipitation probability over the next ~2 h, %, or null if the source omits it. */
  rainChance: z.number().nullable(),
  /** Current wind speed, m/s. */
  windSpeed: z.number(),
  /** Current wind gusts, m/s — the number that matters for drones. */
  windGusts: z.number(),
  /** Wind direction the wind comes FROM, degrees (0 = N, 90 = E). For the widget arrow. */
  windDirection: z.number(),
  /** Total cloud cover, %. */
  cloudCover: z.number(),
  /** Convective available potential energy, J/kg — thunderstorm-risk proxy. */
  cape: z.number(),
  /** WMO weather code (0 clear … 95/96/99 thunderstorm). */
  weatherCode: z.number().int(),
  /** The flight light and its reason, computed from the above (flightAdvice). */
  flight: z.object({ advice: FlightAdvice, reason: FlightReason }),
  /** When the backend last fetched from Open-Meteo (UTC epoch seconds), for "updated X ago". */
  fetchedAt: z.number().int(),
});
export type WeatherForecast = z.infer<typeof WeatherForecast>;
